import type { MediaAccessErrorSubType } from '@openphone/desktop-client'
import type { ReactNode } from 'react'

import { Link } from '@src/component/link'
import Typography from '@ui/Typography'

interface UserMediaDeviceErrorPromptContext {
  mediaDevice: { capitalized: string; lowercase: string }
  originalError: Error
}

interface UserMediaDeviceErrorPrompt {
  title: string
  message: ReactNode
}

type CreateUserMediaDeviceErrorPrompt = (
  ctx: UserMediaDeviceErrorPromptContext,
) => UserMediaDeviceErrorPrompt

type UserMediaDeviceErrorMap = Readonly<
  Record<MediaAccessErrorSubType, CreateUserMediaDeviceErrorPrompt>
>

// eslint-disable-next-line react-refresh/only-export-components
function SupportLink({ sectionId }: { sectionId: string }) {
  return (
    <Link
      href={`https://support.openphone.com/troubleshooting/mic-access-issues#${sectionId}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Visit our Help Center
    </Link>
  )
}

const userMediaDeviceErrorMap: UserMediaDeviceErrorMap = {
  /**
   * Treat denied as an error because anything other than "granted" or "prompt" or "not-determined" is a failure case.
   */
  denied: ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} access denied`,
    message: (
      <>
        <Typography component="p">
          Looks like you didn&apos;t give Quo permission to use your{' '}
          {mediaDevice.lowercase}. Without access, we won&apos;t be able to connect your
          {mediaDevice.lowercase}.
        </Typography>
        <Typography component="p">
          To fix this, check your device settings and make sure {mediaDevice.lowercase}{' '}
          access is allowed for Quo. On macOS, you might need to restart the app after
          updating your settings.
        </Typography>
        <Typography component="p">
          Need help? <SupportLink sectionId="microphone-access-denied" /> for step-by-step
          instructions.
        </Typography>
      </>
    ),
  }),

  /**
   * Thrown if one or more of the requested source devices cannot be used at this time.
   * This will happen if the browsing context is insecure (that is, the page was loaded using HTTP rather than HTTPS).
   * It also happens if the user has specified that the current browsing instance is not permitted access to the device,
   * the user has denied access for the current session, or the user has denied all access to user media devices globally.
   * On browsers that support managing media permissions with Permissions Policy, this error is returned if
   * Permissions Policy is not configured to allow access to the input source(s).
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
   */
  'not-allowed': ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} access blocked`,
    message: (
      <>
        <Typography component="p">
          Your browser isn&apos;t letting Quo use your {mediaDevice.lowercase}. This can
          happen if:
        </Typography>
        <ul>
          <li>You clicked &ldquo;Block&rdquo; when asked for permission</li>
          <li>Your browser settings are preventing access</li>
          <li>You&apos;ve denied access before and the browser remembered</li>
          <li>The page tried to access your devices automatically</li>
        </ul>
        <Typography component="p">
          To fix this, update your browser settings to allow access. We recommend
          refreshing the page afterward. Need help updating your settings?{' '}
          <SupportLink sectionId="microphone-access-blocked" />
        </Typography>
      </>
    ),
  }),

  /**
   * Although the user and operating system both granted access to the hardware device, and
   * no hardware issues occurred that would cause a NotReadableError DOMException, throw if
   * some problem occurred which prevented the device from being used.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  aborted: ({ mediaDevice }) => ({
    title: `Something interrupted your ${mediaDevice.lowercase}`,
    message: (
      <>
        <Typography component="p">
          We tried to connect to your {mediaDevice.lowercase}, but the process was cut off
          — likely by your browser or system. This can happen if:
        </Typography>
        <ul>
          <li>You changed devices while things were loading</li>
          <li>The page lost focus or refreshed mid-request</li>
          <li>Your browser canceled access for another reason</li>
        </ul>
        <Typography component="p">
          Try again and see if it works. If the issue keeps happening, refresh the page or
          restart your browser. Still stuck?{' '}
          <SupportLink sectionId="something-interrupted-your-microphone" />
        </Typography>
      </>
    ),
  }),

  /**
   * Thrown if current document is not fully active.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  'invalid-state': ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} temporarily unavailable`,
    message: (
      <>
        <Typography component="p">
          It looks like Quo tried to use your {mediaDevice.lowercase} before the page was
          fully active. This can happen if:
        </Typography>
        <ul>
          <li>You&apos;re in a background tab</li>
          <li>The page isn&apos;t fully loaded or focused</li>
          <li>Your browser blocked access due to security restrictions</li>
        </ul>
        <Typography component="p">
          To fix this, make sure the tab is active and fully loaded, then try again.
        </Typography>
        <Typography component="p">
          Still not working?{' '}
          <SupportLink sectionId="microphone-temporarily-unavailable" />
        </Typography>
      </>
    ),
  }),

  /**
   * Thrown if the list of constraints specified is empty, or has all constraints set to false.
   * This can also happen if you try to call getUserMedia() in an insecure context, since
   * navigator.mediaDevices is undefined in an insecure context.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  'type-error': ({ mediaDevice }) => ({
    title: `We ran into a technical issue`,
    message: (
      <>
        <Typography component="p">
          Something went wrong while trying to access your {mediaDevice.lowercase} — but
          it&apos;s not your fault. This looks like an internal error on our end. Your
          {mediaDevice.lowercase} is likely working just fine.
        </Typography>
        <Typography component="p">
          We&apos;ve been notified and are looking into it. In the meantime, try again or
          visit our <SupportLink sectionId="we-ran-into-a-technical-issue" /> if you need
          a hand.
        </Typography>
      </>
    ),
  }),

  /**
   * Thrown if the specified constraints resulted in no candidate devices which met the criteria requested.
   * The error is an object of type OverconstrainedError, and has a constraint property whose string value is
   * the name of a constraint which was impossible to meet, and a message property containing a human-readable string
   * explaining the problem.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  overconstrained: ({ mediaDevice }) => ({
    title: `We couldn’t find a working ${mediaDevice.lowercase}`,
    message: (
      <>
        <Typography component="p">
          Quo tried to connect to your microphone, but none of the available devices met
          the requirements. This might happen if:
        </Typography>
        <ul>
          <li>No {mediaDevice.lowercase} is connected</li>
          <li>The connected {mediaDevice.lowercase} isn&apos;t working properly</li>
          <li>Your browser or system doesn&apos;t recognize it</li>
        </ul>
        <Typography component="p">
          Please check your {mediaDevice.lowercase} connection and try again.
        </Typography>
        <Typography component="p">
          Need help troubleshooting?{' '}
          <SupportLink sectionId="we-couldn%E2%80%99t-find-a-working-microphone" />
        </Typography>
      </>
    ),
  }),

  /**
   * A Restricted error means even if the user has granted access, the operating system
   * has restricted access to the microphone. this could be for a variety of reasons
   * such as environment policies (MDM, parental controls, etc.)
   */
  restricted: ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} access is restricted`,
    message: (
      <>
        <Typography component="p">
          Your operating system is blocking Quo from using your {mediaDevice.lowercase}{' '}
          due to system settings or policies.
          <br />
          This can happen if:
        </Typography>
        <ul>
          <li>macOS Screen Time limits access for your account</li>
          <li>Your device is managed by an organization with restrictions in place</li>
          <li>Parental control software is preventing use</li>
        </ul>
        <Typography component="p">
          To fix this, check your system privacy settings or contact your administrator to
          update permissions. Need help?{' '}
          <SupportLink sectionId="microphone-access-is-restricted" />
        </Typography>
      </>
    ),
  }),

  /**
   * Thrown if user media support is disabled on the Document on which getUserMedia() was called.
   * The mechanism by which user media support is enabled and disabled is left up to the individual user agent.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  security: ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} access blocked by browser security settings`,
    message: (
      <>
        <Typography component="p">
          Your browser blocked access due to a security restriction — but this isn&apos;t
          a problem with your devices. <br />
          This looks like an issue on our side, and we&apos;ve already been notified. Your
          {mediaDevice.lowercase} should be working normally.
          <br />
          Please try again in a moment, or{' '}
          <SupportLink sectionId="microphone-access-blocked-by-browser-security-settings" />{' '}
          if you need more info.
        </Typography>
      </>
    ),
  }),

  /**
   * Thrown if no media tracks of the type specified were found that satisfy the given constraints.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  'not-found': ({ mediaDevice }) => ({
    title: `No ${mediaDevice.lowercase} found`,
    message: (
      <>
        <Typography component="p">
          We couldn’t detect any connected {mediaDevice.lowercase}. This can happen if:
        </Typography>
        <ul>
          <li>No {mediaDevice.lowercase} is plugged in or recognized</li>
          <li>Your browser hasn’t refreshed device info yet</li>
        </ul>
        <Typography component="p">
          Try reconnecting your {mediaDevice.lowercase} and refreshing the page. If it
          still doesn’t appear, check your system sound or input settings.
        </Typography>
        <Typography component="p">
          Need help? <SupportLink sectionId="no-microphone-found" />
        </Typography>
      </>
    ),
  }),

  /**
   * Thrown if, although the user granted permission to use the matching devices, a hardware error occurred at the operating system, browser, or Web page level which prevented access to the device.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
   */
  'not-readable': ({ mediaDevice }) => ({
    title: `A hardware or operating system error occurred while attempting to access your ${mediaDevice.lowercase}`,
    message: (
      <>
        <Typography component="p">
          Quo has permission, but something on your device is blocking access. This might
          happen if:
        </Typography>
        <ul>
          <li>Another app (like Zoom or Teams) is using your {mediaDevice.lowercase}</li>
          <li>Your {mediaDevice.lowercase} is disconnected or has a hardware issue</li>
          <li>Your system privacy settings are preventing access</li>
        </ul>
        <Typography component="p">
          To fix this, close any apps that might be using your {mediaDevice.lowercase},
          then try again. If that doesn’t work, check your system privacy settings or
          restart your device.
        </Typography>
        <Typography component="p">
          Need a hand?{' '}
          <SupportLink sectionId="a-hardware-or-operating-system-error-occurred-while-attempting-to-access-your-microphone" />
        </Typography>
      </>
    ),
  }),

  /**
   * Thrown if, although the user granted permission to use the matching devices, a hardware error occurred at the operating system, browser, or Web page level which prevented access to the device.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
   */
  unknown: ({ mediaDevice }) => ({
    title: `We couldn’t confirm your ${mediaDevice.lowercase} status`,
    message: (
      <>
        <Typography component="p">
          Something went wrong while checking your device permissions. This can happen if:
        </Typography>
        <ul>
          <li>Your operating system doesn&apos;t report access status</li>
          <li>There was a temporary issue with your browser or OS</li>
          <li>Your device was just plugged in and isn&apos;t fully ready yet</li>
        </ul>
        <Typography component="p">
          Make sure Quo has permission to use your {mediaDevice.lowercase} in your system
          and browser settings, then try again.
        </Typography>
        <Typography component="p">
          Need help checking your permissions?{' '}
          <SupportLink sectionId="we-couldn%E2%80%99t-confirm-your-microphone-access" />
        </Typography>
      </>
    ),
  }),
}

// eslint-disable-next-line import-x/prefer-default-export
export function getUserMediaDeviceErrorPrompt(
  subType: MediaAccessErrorSubType,
  ctx: UserMediaDeviceErrorPromptContext,
): UserMediaDeviceErrorPrompt {
  return userMediaDeviceErrorMap[subType](ctx)
}
